<!-- eslint-disable vue/valid-v-slot -->
<template>
    <TemplateModalEx v-model:show="computedShow" class="googleAccountAgentSettingEditDialogDetails">
        <template #title>編集</template>
        <template #body>
            <GoogleAccountAgentSettingEdit v-if="computedShow" :mode=1 ref="editComponentRef"
                :targetAssets="targetAssets" v-model:settingData="configSettingData" />
        </template>
        <template #footer>
            <div class="d-flex flex-row justify-content-center align-items-center">
                <div class="googleAccountAgentSettingEditDialogFooter">
                    <button class="btn-scv-blue margin-r" @click="onApply">OK</button>
                    <button class="btn-scv-white" @click="onClose">キャンセル</button>
                </div>
            </div>
        </template>
    </TemplateModalEx>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { TemplateModalEx } from 'lib-vue-v2/components';
import GoogleAccountAgentSettingEdit from '@/views/Mobile/Android/GoogleAccountSetting/GoogleAccountAgentSettingEdit.vue';
import { AndroidSimpleAsset } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAsset';
import { AMAPI_WorkAccountSetupConfig } from '@/models/Mobile/Android/AndroidManagementV1Schema';
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants';
import { cloneDeep } from 'lodash';

// --- Props定義 ---
interface Props {
    /** ダイアログ表示フラグ */
    show: boolean;
    /** 大元で選択された編集対象の端末リスト */
    targetAsset?: AndroidSimpleAsset;
    /** 選択された初期設定データ */
    initialSettingData?: AMAPI_WorkAccountSetupConfig;
}

const props = withDefaults(defineProps<Props>(), {
    targetAsset: undefined,
    initialSettingData: undefined
});

// --- Emits定義 ---
interface Emits {
    (event: "update:show", newValue: boolean): void;
    (event: "apply", settingData: AMAPI_WorkAccountSetupConfig): void;
}

const emit = defineEmits<Emits>();

// --- Refs ---
const editComponentRef = ref<InstanceType<typeof GoogleAccountAgentSettingEdit> | null>(null);

// 表示非表示の切り替え
const refShow = ref(false);
const computedShow = computed({
    get: () => refShow.value,
    set: (newValue: boolean) => emit("update:show", newValue)
});

// デフォルトの設定オブジェクトを生成して返す共通関数
const getDefaultSettings = (): AMAPI_WorkAccountSetupConfig => {
    return {
        authenticationType: schemaConstants.AMAPI_AuthenticationType_Enums.authentication_type_not_enforces,
        requiredAccountEmail: ''
    };
};

// 編集ダイアログで保持するアカウント設定データの退避・双方向バインド用オブジェクト
const configSettingData = ref<AMAPI_WorkAccountSetupConfig>(getDefaultSettings());

// 表示情報の監視（起動・終了）
watch(() => props.show, (newVal) => {
    if (newVal) {
        if (props.initialSettingData) configSettingData.value = cloneDeep(props.initialSettingData);
        else configSettingData.value = getDefaultSettings();
    }
    refShow.value = newVal;
}, { immediate: true });

const targetAssets = computed(() => {
    if (props.targetAsset === undefined) return [];
    return [props.targetAsset];
});



// 適用ボタン（OKボタン）押下
const onApply = (): void => {
    // 子コンポーネントのバリデーションチェック
    if (!editComponentRef.value) return;
    const isValid = editComponentRef.value.validateCheck();
    if (!isValid) return;

    emit("apply", configSettingData.value);
    emit("update:show", false);
}

// 閉じるボタン押下
const onClose = (): void => {
    emit("update:show", false);
}
</script>

<style lang="scss" scoped>
.googleAccountAgentSettingEditDialogDetails {
    :deep(.modal-content) {
        width: 960px;
        max-width: none;
        max-height: none;
        display: flex;
        flex-direction: column;
        overflow: hidden; // 枠外への飛び出しを防止
    }

    // ボディ部全体を縦フレックスにし、中身を自動配分する
    :deep(.modal-body) {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0; // 高さが潰れたり溢れたりするのを防ぐ
    }

    .googleAccountAgentSettingEditDialogFooter {
        display: flex;
        flex-direction: row;
        align-items: center;

        .margin-r {
            margin-right: 24px;
        }
    }
}
</style>
